import { Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { TokenManagerService } from '../services/token-manager.service';
import { AuthErrorService } from '../services/auth-error.service';
import { Organization, UserService } from '../services/user.service';
import { MsalService } from '@azure/msal-angular';
import { environment } from '../../../environments/environment';

const REQUIRED_SCOPES_ASSETWORKS = ['LogIn'];
const ENVIRONMENT_SCOPES = [
  'Environment.DEV.Scope',
  'Environment.TST.Scope',
  'Environment.UAT.Scope',
  'Environment.PRD.Scope',
];

@Injectable({ providedIn: 'root' })
export class ScopesGuard implements CanActivate {
  constructor(
    private tokenManagerService: TokenManagerService,
    private userService: UserService,
    private authErrorService: AuthErrorService,
    private msalService: MsalService,
    private router: Router
  ) {}

  async canActivate(): Promise<boolean | UrlTree> {
    // Check if we already have token cached, check validity of token if so
    const currentToken = this.tokenManagerService.getAccessToken();

    if (currentToken && this.tokenManagerService.getScopes().size > 0) {
      return this.validateScopes() || this.denyAndRedirectToLogin();
    }

    // On initial msal login, the token isn't currently cached within the tokenManagerService,
    // We use Msal to directly check for its existence and run the same validity check above
    const hasMsalToken = await this.tryAcquireMsalToken();
    if (hasMsalToken) {
      return this.validateScopes() || this.denyAndRedirectToLogin();
    }

    return this.denyAndRedirectToLogin();
  }

  private denyAndRedirectToLogin(): UrlTree {
    this.authErrorService.setErrorMessage(
      'You are not authorized to access this application'
    );

    const organizationId: string =
      this.userService.getUserInfo()?.organizationId || '';

    this.tokenManagerService.clearAccessToken();
    this.userService.clearOrganization();

    return this.router.createUrlTree(['/logout'], {
      queryParams: {
        reason: 'Scope Mismatch',
        organizationId: organizationId,
      },
    });
  }

  private validateScopes(): boolean {
    const organization: Organization | null =
      this.userService.getOrganization();
    let hasRequiredScopes: boolean = true;

    // Assetworks employees need these scopes
    if (organization === 'assetworks-employee') {
      hasRequiredScopes = this.tokenManagerService.containsAllScopes(
        REQUIRED_SCOPES_ASSETWORKS
      );
    }

    const hasEnvironmentScopes: boolean =
      this.tokenManagerService.containsAnyScopes(ENVIRONMENT_SCOPES);

    return hasRequiredScopes && hasEnvironmentScopes;
  }

  private async tryAcquireMsalToken(): Promise<boolean> {
    const accounts = this.msalService.instance.getAllAccounts();
    if (!accounts.length) return false;

    try {
      const result = await this.msalService.instance.acquireTokenSilent({
        scopes: environment.apiConfig.scopes,
        account: this.msalService.instance.getActiveAccount() ?? accounts[0],
      });
      this.tokenManagerService.setAccessToken(result.accessToken);
      return true;
    } catch {
      return false;
    }
  }
}
